import { useMutation, useApolloClient } from '@apollo/client/react'

import { toast } from '@buffer-mono/popcorn'
import { BufferTrackerReact } from '@buffer-mono/tracking-plan'

import type { ResultOf, VariablesOf } from '~publish/graphql'
import type { Profile } from '~publish/legacy/profile-sidebar/types'
import { GET_CUSTOM_LINKS } from '~publish/pages/Channel/ChannelShopGrid/ChannelShopGrid'

import {
  ADD_NEW_CUSTOM_LINK,
  DELETE_CUSTOM_LINK,
  SWAP_CUSTOM_LINKS,
  UPDATE_CUSTOM_LINK,
  UPDATE_CUSTOM_LINK_COLORS,
  UPDATE_POST_LINK,
} from './queries'
import { getChannelProperties } from '../util/index'

/**
 * Concrete Shop Grid payload shape, derived from the `GET_CUSTOM_LINKS` query
 * that reads it back. The mutations select the same payload as one branch of a
 * union, and deriving from the query keeps this in lockstep with the shape the
 * cache is written with below.
 */
type ShopGridCustomLinksPayload = NonNullable<
  ResultOf<typeof GET_CUSTOM_LINKS>['shopGridCustomLinks']
>

type AddShopGridCustomLinkInput = VariablesOf<
  typeof ADD_NEW_CUSTOM_LINK
>['input']
type DeleteShopGridCustomLinkInput = VariablesOf<
  typeof DELETE_CUSTOM_LINK
>['input']
type UpdateShopGridCustomLinkInput = VariablesOf<
  typeof UPDATE_CUSTOM_LINK
>['input']
type SwapShopGridCustomLinksInput = VariablesOf<
  typeof SWAP_CUSTOM_LINKS
>['input']
type UpdateShopGridPostLinkInput = VariablesOf<typeof UPDATE_POST_LINK>['input']
type UpdateShopGridPostLinkPayload = ResultOf<
  typeof UPDATE_POST_LINK
>['updateShopGridPostLink']

function updateApolloCache({
  apollo,
  channelId,
  data,
}: {
  apollo: ReturnType<typeof useApolloClient>
  channelId: string
  data: {
    links?: ShopGridCustomLinksPayload['links']
    color?: ShopGridCustomLinksPayload['color']
    contrastColor?: ShopGridCustomLinksPayload['contrastColor']
  }
}): void {
  const currentData = apollo.readQuery({
    query: GET_CUSTOM_LINKS,
    variables: {
      input: {
        channelId,
      },
    },
  })

  apollo.writeQuery({
    query: GET_CUSTOM_LINKS,
    data: {
      shopGridCustomLinks: {
        ...currentData?.shopGridCustomLinks,
        ...data,
      },
    },
    variables: {
      input: {
        channelId,
      },
    },
  })
}

export function useDeleteCustomLink({
  channelId,
}: {
  channelId: string
}): ReturnType<
  typeof useMutation<
    { deleteShopGridCustomLink: Pick<ShopGridCustomLinksPayload, 'links'> },
    { input: DeleteShopGridCustomLinkInput }
  >
> {
  const apollo = useApolloClient()

  return useMutation<
    { deleteShopGridCustomLink: Pick<ShopGridCustomLinksPayload, 'links'> },
    { input: DeleteShopGridCustomLinkInput }
  >(DELETE_CUSTOM_LINK, {
    onCompleted: (data) => {
      toast.success('Nice! Your changes have been saved.')

      updateApolloCache({
        apollo,
        channelId,
        data: {
          links: data.deleteShopGridCustomLink.links,
        },
      })
    },
    onError: () => {
      toast.error('There was an error saving your changes!')
    },
  })
}

export function useAddNewCustomLink({
  channelId,
}: {
  channelId: string
}): ReturnType<
  typeof useMutation<
    { addShopGridCustomLink: Pick<ShopGridCustomLinksPayload, 'links'> },
    { input: AddShopGridCustomLinkInput }
  >
> {
  const apollo = useApolloClient()

  return useMutation<
    { addShopGridCustomLink: Pick<ShopGridCustomLinksPayload, 'links'> },
    { input: AddShopGridCustomLinkInput }
  >(ADD_NEW_CUSTOM_LINK, {
    onCompleted: (data) => {
      toast.success('Nice! Your changes have been saved.')

      updateApolloCache({
        apollo,
        channelId,
        data: {
          links: data.addShopGridCustomLink.links,
        },
      })
    },
    onError: () => {
      toast.error('There was an error saving your changes!')
    },
  })
}

export function useUpdateCustomLinkColors({
  channelId,
}: {
  channelId: string
}): ReturnType<
  typeof useMutation<
    ResultOf<typeof UPDATE_CUSTOM_LINK_COLORS>,
    VariablesOf<typeof UPDATE_CUSTOM_LINK_COLORS>
  >
> {
  return useMutation<
    ResultOf<typeof UPDATE_CUSTOM_LINK_COLORS>,
    VariablesOf<typeof UPDATE_CUSTOM_LINK_COLORS>
  >(UPDATE_CUSTOM_LINK_COLORS, {
    refetchQueries: [
      {
        query: GET_CUSTOM_LINKS,
        variables: {
          input: {
            channelId,
          },
        },
      },
    ],
  })
}

export function useUpdatePostLink({
  refetch,
  profile,
}: {
  refetch: () => void
  profile: Profile
}): ReturnType<
  typeof useMutation<
    { updateShopGridPostLink: UpdateShopGridPostLinkPayload },
    { input: UpdateShopGridPostLinkInput }
  >
> {
  return useMutation<
    { updateShopGridPostLink: UpdateShopGridPostLinkPayload },
    {
      input: UpdateShopGridPostLinkInput
    }
  >(UPDATE_POST_LINK, {
    onCompleted: (data, options = {}) => {
      toast.success('Nice! Your changes have been saved.')

      const input = options.variables?.input

      if (input) {
        BufferTrackerReact.shopGridPostURLUpdated({
          ...getChannelProperties(profile),
          postId: input.postId,
          url: input.link ?? '',
        })
      }

      refetch()
    },
    onError: () => {
      toast.error('There was an error saving your changes!')
    },
  })
}

export function useEditCustomLink({
  channelId,
}: {
  channelId: string
}): ReturnType<
  typeof useMutation<
    { updateShopGridCustomLink: ShopGridCustomLinksPayload },
    { input: UpdateShopGridCustomLinkInput }
  >
> {
  const apollo = useApolloClient()

  return useMutation<
    { updateShopGridCustomLink: ShopGridCustomLinksPayload },
    { input: UpdateShopGridCustomLinkInput }
  >(UPDATE_CUSTOM_LINK, {
    onCompleted: (data) => {
      toast.success('Nice! Your changes have been saved.')

      updateApolloCache({
        apollo,
        channelId,
        data: {
          color: data.updateShopGridCustomLink.color,
          contrastColor: data.updateShopGridCustomLink.contrastColor,
        },
      })
    },
    onError: () => {
      toast.error('There was an error saving your changes!')
    },
  })
}

export function useSwapCustomLinks({
  channelId,
}: {
  channelId: string
}): ReturnType<
  typeof useMutation<
    { swapShopGridCustomLinks: Pick<ShopGridCustomLinksPayload, 'links'> },
    { input: SwapShopGridCustomLinksInput }
  >
> {
  const apollo = useApolloClient()

  return useMutation<
    { swapShopGridCustomLinks: Pick<ShopGridCustomLinksPayload, 'links'> },
    { input: SwapShopGridCustomLinksInput }
  >(SWAP_CUSTOM_LINKS, {
    onCompleted: (data) => {
      toast.success('Nice! Your changes have been saved.')

      updateApolloCache({
        apollo,
        channelId,
        data: {
          links: data.swapShopGridCustomLinks.links,
        },
      })
    },
    onError: () => {
      toast.error('There was an error saving your changes!')
    },
  })
}
